import axiosInstance from "../services/axiosInstance";  
export const kpiAPI = {
  // Save a new draft
  saveDraft: (data) => axiosInstance.post("/api/kpi/draft", data),
  // Get all drafts
  getDrafts: () => axiosInstance.get("/api/kpi/drafts"),
  // Update a draft by ID
  updateDraft: (id, data) => axiosInstance.put(`/api/kpi/draft/${id}`, data),
  // Submit a draft by ID
  submitDraft: (id, data) =>
    axiosInstance.post(`/api/kpi/draft/${id}/submit`, data),
  // Delete/Discard a draft by ID
  deleteDraft: (id) => axiosInstance.delete(`/api/kpi/draft/${id}`),
  // Get KPI by status
  getByStatus: (status) => axiosInstance.get(`/api/kpi/status/${status}`),
  // Get status summary
  getStatusSummary: () => axiosInstance.get("/api/kpi/status-summary"),
};
// Document APIs
export const documentService = {
  fetchDocuments: () =>
    axiosInstance.get("/api/documents").then((res) => res.data),
  fetchDocumentFile: (docId) =>
    axiosInstance.get(`/api/documents/${docId}/file`, { responseType: "blob" }),
  uploadDocument: (formData) =>
    axiosInstance.post("/api/documents", formData),
  deleteDocument: (docId) => axiosInstance.delete(`/api/documents/${docId}`),
};
// Project APIs
export const projectService = {
  getAllProjects: () =>
    axiosInstance.get("/api/projects/cards?limit=100").then((res) => res.data),
  
  // Create a new project
  createProject: (projectData) =>
    axiosInstance.post("/api/projects", projectData).then((res) => res.data),
  
  // Get project by ID
  getProjectById: (id) =>
    axiosInstance.get(`/api/projects/${id}`).then((res) => res.data),
  
  // Update project
  updateProject: (id, projectData) =>
    axiosInstance.put(`/api/projects/${id}`, projectData).then((res) => res.data),
  
  // Delete project
  deleteProject: (id) =>
    axiosInstance.delete(`/api/projects/${id}`).then((res) => res.data)
};
// Profile APIs
export const profileService = {
  getProfile: () =>
    axiosInstance.get("/api/employees/profile").then((res) => res.data),
  updateProfile: (employeeId, data) =>
    // Accept either JSON or FormData depending on caller
    axiosInstance.put(`/api/employees/${employeeId}`, data, {
      headers: data instanceof FormData ? { 'Content-Type': 'multipart/form-data' } : undefined
    }).then((res) => res.data),
  updateProfilePhoto: (employeeId, formData) =>
    axiosInstance.put(`/api/employees/${employeeId}/profile-photo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then((res) => res.data),
};
// Update the clientService in your API services file
export const clientService = {
  // Get all clients
  getAllClients: () =>
    axiosInstance.get("/api/clients").then((res) => res.data),
  
  // Get clients for dropdown
  getClients: () =>
    axiosInstance.get("/api/clients/get-clients").then((res) => res.data),
  
  // Get single client by ID
  getClientById: (id) =>
    axiosInstance.get(`/api/clients/${id}`).then((res) => res.data),
  
  // Create new client
  createClient: (data) => axiosInstance.post("/api/clients", data, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  
  // Update client
  updateClient: (id, data) => axiosInstance.put(`/api/clients/${id}`, data, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  
  // Delete client
  deleteClient: (id) => axiosInstance.delete(`/api/clients/${id}`),
};

export const announcementService = {
  getPublished: () => axiosInstance.get("/api/announcements"),
  getAll: () => axiosInstance.get("/api/announcements/manage"),
  create: (data) => axiosInstance.post("/api/announcements", data, {
    headers: { "Content-Type": "multipart/form-data" },
  }),
  update: (id, data) => axiosInstance.put(`/api/announcements/${id}`, data, {
    headers: { "Content-Type": "multipart/form-data" },
  }),
  remove: (id) => axiosInstance.delete(`/api/announcements/${id}`),
};

export const dailyTaskKpiService = {
  getByDate: (date, employeeId) =>
    axiosInstance.get("/api/tasks/kpi/daily", {
      params: { date, employee_id: employeeId },
    }),
};

// Admin-only AI assistant widget
export const chatbotService = {
  getStatus: () => axiosInstance.get("/api/chatbot/status").then((res) => res.data),
  sendMessage: (message, history) =>
    axiosInstance.post("/api/chatbot/message", { message, history }).then((res) => res.data),
};

// Project dropdown for EmployeeKpi
export const projectDropdownService = {
  getProjects: () =>
    axiosInstance.get("/api/timesheets/options").then((res) => {
      // The endpoint returns { data: [...] }
      if (res.data && Array.isArray(res.data.data)) {
        return res.data.data;
      }
      return [];
    }),
};
// Authentication APIs
export const authService = {
  login: (username, password) =>
    axiosInstance.post("/api/auth/login", { username, password }).then((res) => res),

  // Step 2 of login: verify the OTP emailed by login() above, then get tokens.
  verifyLoginOtp: (email_id, otp) =>
    axiosInstance.post("/api/auth/login/verify-otp", { email_id, otp }).then((res) => res),

  // These 3 were missing the /api/auth prefix (authRoutes is only mounted at
  // /api/auth) - every "forgot password" attempt 404'd on the very first
  // step, Send OTP, before ever reaching the backend.
  sendOTP: (email) => axiosInstance.post("/api/auth/send-otp", { email_id: email }),

  validateOTP: (email, otp) =>
    axiosInstance.post("/api/auth/validate-otp", { email_id: email, otp }),

  resetPassword: (email, newPassword, confirmPassword, otp) =>
    axiosInstance.post('/api/auth/reset-password', { email_id: email, newPassword, confirmPassword, otp }),

  // Properly logout and blacklist token on backend
  logout: async () => {
    try {
      const TokenManager = (await import('./tokenManager')).default;
      const token = TokenManager.getAccessToken();
      const sessionId = TokenManager.getSessionId();
      const refreshToken = TokenManager.getRefreshToken();

      if (token) {
        // Call backend logout endpoint to blacklist token
        await axiosInstance.post('/api/auth/logout', {
          login_session_id: sessionId,
          request_refresh_token: refreshToken
        }).catch((err) => {
          // Even if logout API fails, still clear storage
          console.warn('Logout API call failed:', err.message);
        });
      }

      // Clear tokens (from sessionStorage/localStorage)
      TokenManager.clearTokens();
      return { success: true };
    } catch (error) {
      console.error('Logout error:', error);
      // Still clear tokens even if API call fails
      const TokenManager = (await import('./tokenManager')).default;
      TokenManager.clearTokens();
      return { success: true };
    }
  },
  
  changePassword: (currentPassword, newPassword, confirmPassword) =>
    axiosInstance.post('/api/auth/change-password', { currentPassword, newPassword, confirmPassword }),
};

// Update the employeeService in your existing API services file
export const employeeService = {
  getAllEmployees: () => axiosInstance.get("/api/employees"),

  getEmployeesAnn: () => axiosInstance.get("/api/employees-ann"),

  // Employees visible to the authenticated manager. The API derives the
  // manager identity from the JWT, avoiding mismatches between codes and IDs.
  getReportingEmployees: () =>
    axiosInstance.get("/api/employees/reporting"),

  getHodReportingEmployees: () =>
    axiosInstance.get("/api/employees/hod-reporting"),

  getEmployeesForTask: (managerId) =>
    axiosInstance.get(`/api/tasks/employee/${managerId}`),

  getEmployeeDetails: (employeeId) =>
    axiosInstance.get(`/api/employees/${employeeId}`),

  // Updated createEmployee to match your router.post endpoint
  createEmployee: (data) => axiosInstance.post("/api/employees", data, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),

  updateEmployee: (employeeId, data) =>
    axiosInstance.put(`/api/employees/${employeeId}`, data),

  deleteEmployee: (employeeId) =>
    axiosInstance.delete(`/api/employees/${employeeId}`),
};

// Task APIs
export const taskService = {
  createTask: (taskData) => axiosInstance.post("/api/tasks", taskData),

  getAssignedTasks: (managerId) =>
    axiosInstance.get(`/api/tasks/assigned-by/${managerId}`),

  // Admin/super_admin only - every task across the org.
  getAllTasks: () => axiosInstance.get('/api/tasks/all'),
  downloadDailyReport: (date) => axiosInstance.get('/api/tasks/daily-report', { params: { date }, responseType: 'blob' }),

  getMyTasks: (employeeId) =>
    axiosInstance.get(`/api/tasks/employee/${employeeId}`),

  getReportingEmployees: (managerId) =>
    axiosInstance.get(`/api/tasks/employees/${managerId}`),

  updateTask: (taskId, data) => axiosInstance.put(`/api/tasks/${taskId}`, data),

  reassignTask: (taskId, data) => axiosInstance.put(`/api/tasks/${taskId}/reassign`, data),

  deleteTask: (taskId) =>
    axiosInstance.delete(`/api/tasks/${taskId}`),

  deleteMultipleTasks: (taskIds) =>
    axiosInstance.post("/delete-multiple-tasks", { task_ids: taskIds }),

  updateTimesheetTask: (data) =>
    axiosInstance.post("/update-timesheet-task", data),
};

// KPI APIs
export const kpiService = {
  getKPIs: (employeeId) => axiosInstance.get(`/api/kpi/${employeeId}`),

  createKPI: (data) => axiosInstance.post("/api/kpi", data),

  updateKPI: (kpiId, data) => axiosInstance.put(`/api/kpi/${kpiId}`, data),

  // Submit KPI (calls /submit endpoint)
  submitKPI: (data) => axiosInstance.post("/api/kpi/submit", data),

  // NEW: Submit multiple KPIs in a single batch request (calls /submit-batch endpoint)
  // Expected payload: { kpis: [{ projectName, clientName, description, ... }, ...] }
  submitBatchKPIs: (kpisData) => axiosInstance.post("/api/kpi/submit-batch", { kpis: kpisData }),

  // Custom: Fetch weekly KPI data
  // Backend expects POST /api/kpi/fetch-data-weekly with { week: "YYYY-Www" }
  getWeeklyKPI: (week) =>
    axiosInstance
      .post("/api/kpi/fetch-data-weekly", { week })
      .then((res) => res.data),

  // Custom: Fetch monthly KPI data
  getMonthlyKPI: (month) =>
    axiosInstance
      .post("/api/kpi/fetch-data-monthly", { month })
      .then((res) => res.data),

  // Custom: Fetch KPI Not Submitted
  getKpiNotSubmitted: (week) =>
    axiosInstance
      .post("/api/kpi/not-submitted", { week })
      .then((res) => res.data),

  // Custom: Fetch KPI Not Rated
  getKpiNotRated: (week) =>
    axiosInstance.post("/api/kpi/not-rated", { week }).then((res) => res.data),

  // [EDITED: KPI Task - Mahesh Sharma]
  // Fetch both Submitted and Not Submitted status
  getSubmissionsStatus: (week) => // [EDITED: KPI Task - Mahesh Sharma]
    axiosInstance.get("/api/kpi/submissions-status", { params: { week } }).then((res) => res.data), // [EDITED: KPI Task - Mahesh Sharma]

  // Fetch both Rated and Not Rated status
  getRatingsStatus: (week) => // [EDITED: KPI Task - Mahesh Sharma]
    axiosInstance.get("/api/kpi/ratings-status", { params: { week } }).then((res) => res.data), // [EDITED: KPI Task - Mahesh Sharma]

  // Fetch hierarchical submissions and ratings for management
  getManagementHierarchy: (week) => // [EDITED: KPI Task - Mahesh Sharma]
    axiosInstance.get("/api/kpi/management-hierarchy", { params: { week } }).then((res) => res.data), // [EDITED: KPI Task - Mahesh Sharma]

  // Revoke rating and reopen for re-evaluation
  revokeKPIRating: (id) => // [EDITED: KPI Task - Mahesh Sharma]
    axiosInstance.post(`/api/kpi/revoke-rating/${id}`).then((res) => res.data), // [EDITED: KPI Task - Mahesh Sharma]
};

// Reports APIs
export const reportService = {
  getEmployeeReports: (employeeId) =>
    axiosInstance.get(`/api/reports/employee/${employeeId}`),

  getManagerReports: (managerId) =>
    axiosInstance.get(`/api/reports/manager/${managerId}`),

  getTimesheet: (employeeId, startDate, endDate) =>
    axiosInstance.get("/api/timesheet", {
      params: { employeeId, startDate, endDate },
    }),

  // Fetch general reports (used in Reports page)
  getReports: (filters) =>
    axiosInstance.get("/fetch-reports2", { params: filters }),
};

// Ticket APIs
export const ticketService = {
  getAllTickets: () => axiosInstance.get("/all-ticket"),

  getUnresolvedTickets: () => axiosInstance.get("/unres-ticket"),

  getResolvedTickets: () => axiosInstance.get("/res-ticket"),

  getClosedTickets: () => axiosInstance.get("/closed-ticket"),

  getDeletedTickets: () => axiosInstance.get("/deleted-ticket"),

  createTicket: (data) => axiosInstance.post("/api/tickets", data),

  updateTicket: (ticketId, data) =>
    axiosInstance.put(`/api/tickets/${ticketId}`, data),
};

// Utility APIs
export const utilService = {
  getUpcomingBirthdays: () => axiosInstance.get("/api/dashboard/upcoming-birthdays"),

  getEmployeeOfTheMonth: () => axiosInstance.get("/api/dashboard/employee-of-month"),
  getWorkAnniversary: () => axiosInstance.get("/api/employees-ann"),
  
  // New anniversary endpoints
  getUpcomingAnniversaries: () => axiosInstance.get("/api/dashboard/upcoming-anniversaries"),
  getUpcomingAnniversariesByMonth: (month) => axiosInstance.get(`/api/dashboard/upcoming-anniversaries?month=${month}`),

  getSocialLinks: () => axiosInstance.get("/api/social-links"),

  storeSelection: (data) => axiosInstance.post("/api/store-selection", data),
  // Daily Report submission stub
  submitDailyReport: (data) => axiosInstance.post("/api/daily-report", data),
  // Fetch categories for daily report
  getCategories: () =>
    axiosInstance
      .get("/category-options")
      .then((res) => (Array.isArray(res.data) ? res.data : [])),
};

// Employee KPI APIs (for employee self-service KPI page)
export const employeeKpiService = {
  getEmployeeKpis: () =>
    axiosInstance.get("/api/employee-kpi").then((res) => res.data),
  addEmployeeKpi: (data) =>
    axiosInstance.post("/api/employee-kpi", data).then((res) => res.data),
};

export const employeeKpiViewService = {
  fetchKpiData: () =>
    axiosInstance.get("/api/kpi/fetchKPI").then((res) => res.data),
  fetchKpiById: (id) =>
    axiosInstance.get(`/api/kpi/${id}`).then((res) => res.data),
  getGroupedKpis: (params) =>
    axiosInstance
      .get("/api/kpi/my-kpi-grouped", { params })
      .then((res) => res.data),
  fetchKpisByWeek: (week) =>
    axiosInstance
      .get("/api/kpi/my-kpi-grouped", { params: { week } })
      .then((res) => res.data),
};
// Add this to your existing employeeKpiAdminService object

export const employeeKpiAdminService = {
  getPendingTlKpis: () =>
    axiosInstance
      .get("/api/kpi/weekly-kpi-pending-tl")
      .then((res) => res.data.data), // ✅ extract the array

  getAllTlKpis: () =>
    axiosInstance
      .get("/api/kpi/weekly-kpi-pending-tl", { params: { mode: "all" } })
      .then((res) => res.data),

  getPendingHodKpis: () =>
    axiosInstance
      .get("/api/kpi/weekly-kpi-pending-hod")
      .then((res) => res.data.data || res.data),

  // ✅ NEW: Admin pending KPI endpoint
  getPendingAdminKpis: () =>
    axiosInstance.get("/api/kpi/admin-pending").then((res) => {
      // Handle different response structures
      const data = res.data;
      if (data && data.data) {
        return data.data; // Extract nested data
      }
      if (Array.isArray(data)) {
        return data; // Return array directly
      }
      return []; // Fallback to empty array
    }),

  getKpisByWeek: (week, role = "tl") => {
    // Only TL has a dedicated by-week endpoint
    return axiosInstance
      .get("/api/kpi/kpi/by-week", { params: { week } })
      .then((res) => res.data.data || res.data); // ✅ extract the inner array
  },

  getKpisByWeekForHod: (week) => {
    // Fallback: fetch all pending HOD KPIs and filter by week
    return axiosInstance.get("/api/kpi/weekly-kpi-pending-hod").then((res) => {
      const data = res.data.data || res.data;
      return Array.isArray(data)
        ? data.filter((kpi) => kpi.week_number === week)
        : [];
    });
  },

  // ✅ NEW: Get KPIs by week for admin
  getKpisByWeekForAdmin: (week) => {
    return axiosInstance
      .get("/api/kpi/admin-pending", { params: { week } })
      .then((res) => {
        const data = res.data.data || res.data;
        return Array.isArray(data)
          ? data.filter((kpi) => kpi.week_number === week)
          : [];
      });
  },
};

// Attendance APIs

// Team Lead KPI Review API
export const teamLeadKpiReviewService = {
  submitReview: (payload) =>
    axiosInstance.post("/api/kpi/update-kpi1", payload).then((res) => res.data),
};
// Team Lead: fetch KPI by week
export const teamLeadKpiWeeklyService = {
  getByWeek: (weekno) =>
    axiosInstance
      .post("/api/kpi/weekly-kpi-by-week-tl", { weekno })
      .then((res) => res.data),

  getTlWeeklyRating: (week) =>
    axiosInstance
      .get("/api/kpi/tl-rating/by-week", { params: { week } })
      .then((res) => res.data),
};

// HOD KPI Review Service
export const hodKpiReviewService = {
  submitReview: (payload) =>
    axiosInstance
      .post("api/kpi/hod/update-kpi", payload)
      .then((res) => res.data),
  getByWeek: (weekno) =>
    axiosInstance
      .post("/api/kpi/weekly-kpi-by-week-tl", { weekno })
      .then((res) => res.data),
  getHodWeeklyRating: (week) =>
    // HOD rating endpoint doesn't exist, return null
    Promise.resolve(null),
};
// Dashboard API
export const dashboardService = {
  getRoleDashboard: () =>
    axiosInstance.get("/api/dashboard/cards-counts").then((res) => res.data),
  getMisPerformanceTracker: () =>
    axiosInstance.get("/api/dashboard/mis-performance-tracker").then((res) => res.data),
};

// Admin KPI Review Service
export const adminKpiReviewService = {
  // Submit admin rating for a single KPI
  submitManagerRating: (id, data) =>
    axiosInstance
      .post(`/api/kpi/manager-rate/${id}`, data)
      .then((res) => res.data),

  // Get admin weekly rating
  getAdminWeeklyRating: (week) =>
    axiosInstance
      .get("/api/kpi/admin-rating/by-week", { params: { week } })
      .then((res) => res.data),
};

// Department APIs
export const departmentService = {
  getAllDepartments: () =>
    axiosInstance.get("/api/employees/departments").then(res => res.data),
};

// Reporting Manager APIs
export const reportingManagerService = {
  getAllReportingManagers: () =>
    axiosInstance.get("/api/employees/reporting-managers").then(res => res.data),
};


// Attendance APIs (Check-in/Check-out with Location Tracking)

export const attendanceService = {
  // Basic attendance operations
  checkIn: (latitude, longitude, accuracy, address, deviceInfo) =>
    axiosInstance.post('/api/attendance/check-in', {
      latitude,
      longitude,
      accuracy,
      address,
      deviceInfo
    }).then(res => res.data),

  checkOut: (latitude, longitude, accuracy, address, deviceInfo) =>
    axiosInstance.post('/api/attendance/check-out', {
      latitude,
      longitude,
      accuracy,
      address,
      deviceInfo
    }).then(res => res.data),

  getTodayAttendance: () =>
    axiosInstance.get('/api/attendance/today').then(res => res.data),

  getAttendanceHistory: (days = 30) =>
    axiosInstance.get('/api/attendance/history', { params: { days } }).then(res => res.data),

  checkLocation: (latitude, longitude, accuracy) =>
    axiosInstance.post('/api/attendance/check-location', {
      latitude,
      longitude,
      accuracy
    }).then(res => res.data),

  getGeofenceConfig: () =>
    axiosInstance.get('/api/attendance/geofence-config').then(res => res.data),

  getAttendanceSummary: (fromDate, toDate, department = null) =>
    axiosInstance.get('/api/attendance/summary', {
      params: { fromDate, toDate, department }
    }).then(res => res.data),

  getTodayAllEmployees: () =>
    axiosInstance.get('/api/attendance/all-today').then(res => res.data),

  markAbsent: (employee_id, workDate) =>
    axiosInstance.post('/api/attendance/mark-absent', {
      employee_id,
      workDate
    }).then(res => res.data),

  // New enhanced location services
  getLocationHistory: (fromDate, toDate) =>
    axiosInstance.get('/api/attendance/location-history', {
      params: { fromDate, toDate }
    }).then(res => res.data),

  getTeamLocations: (date, department = null) =>
    axiosInstance.get('/api/attendance/team-locations', {
      params: { date, department }
    }).then(res => res.data),

  getGeofenceViolations: (fromDate, toDate) =>
    axiosInstance.get('/api/attendance/geofence-violations', {
      params: { fromDate, toDate }
    }).then(res => res.data),

  getAttendanceAnalytics: (year, month) =>
    axiosInstance.get('/api/attendance/analytics', {
      params: { year, month }
    }).then(res => res.data),

  getRealTimeLocations: (department = null) =>
    axiosInstance.get('/api/attendance/real-time-locations', {
      params: { department }
    }).then(res => res.data),

  updateGeofenceConfig: (latitude, longitude, radius, officeName) =>
    axiosInstance.put('/api/attendance/geofence-config', {
      latitude,
      longitude,
      radius,
      officeName
    }).then(res => res.data),

  exportAttendanceReport: (fromDate, toDate, department = null, format = 'json') =>
    axiosInstance.get('/api/attendance/export-report', {
      params: { fromDate, toDate, department, format },
      responseType: format === 'csv' ? 'blob' : 'json'
    }).then(res => res.data),
  // Manager endpoints
  getManagerPendingRequests: () =>
    axiosInstance.get('/api/attendance/manager-pending').then((res) => res.data),

  reviewAttendanceRequest: (requestId, payload) =>
    axiosInstance.put(`/api/attendance/request/${requestId}/review`, payload).then((res) => res.data),
  getAttendanceCalendar: (fromDate, toDate, employeeId = null) =>
    axiosInstance.get('/api/attendance/calendar', { params: { fromDate, toDate, employee_id: employeeId } }).then(res => res.data),
  importAttendanceExcel: (formData) =>
    axiosInstance.post('/api/attendance/import-excel', formData, { headers: { 'Content-Type': 'multipart/form-data' } }).then(res => res.data),
};

